import { useEffect, useState } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Button,
    IconButton,
    Typography,
    Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import TestListBlueprintsVersionIcon from "../../assets/iconComponents/TestListBlueprintsVersionIcon";
import TestListVersionIcon from "../../assets/iconComponents/TestListVersionIcon";
import TestListAddVersionIcon from "../../assets/iconComponents/TestListAddVersionIcon";
import TestListAddCycleIcon from "../../assets/iconComponents/TestListAddCycleIcon";
import TestListCycleIcon from "../../assets/iconComponents/TestListCycleIcon";
import RerunIcon from "../../assets/iconComponents/RerunIcon";
import TestsHierarchicalListRow from "./TestsHierarchicalListRow";
import TestsHierarchicalListDropdown from "./TestsHierarchicalListDropdown";
import CreateVersionModal from "../modals/CreateVersionModal";
import EditVersionModal from "../modals/EditVersionModal";
import DeleteTestCaseVersionConfirmationModal from "../modals/DeleteTestCaseVersionConfirmationModal";
import CreateCycleModal from "../modals/CreateCycleModal";
import EditCycleModal from "../modals/EditCycleModal";
import DeleteTestCaseCycleConfirmationModal from "../modals/DeleteTestCaseCycleConfirmationModal";
import CreateFolderModal from "../modals/CreateFolderModal";
import EditFolderModal from "../modals/EditFolderModal";
import DeleteTestCaseFolderConfirmationModal from "../modals/DeleteTestCaseFolderConfirmationModal";
import AddTestsModal from "../modals/AddTestsModal";
import EditTestCaseModal from "../common/EditTestCaseModal";
import DeleteTestCaseFromCycleConfirmationModal from "../modals/DeleteTestCaseFromCycleConfirmationModal";
import { truncate } from "../../utils/formatString";
import PageService from "../../services/PageService";
import JourneyTestCaseService from "../../pages/SingleJourney/components/TestCasesTab/services";
import CreateTestCaseModal from "pages/SingleJourney/components/TestCasesTab/components/CreateTestCaseModal";
import JourneyService from "pages/SingleJourney/services";
import { socketEventEmitter } from "../../socket";
import TaskService from "services/TaskService";
import { useProject } from "context/ProjectContext";
import { TASK_STATUSES, TASK_TYPES, TASK_ALIAS_TYPES } from "../../constants";

const getStatusCounts = (testCases) => {
    const normalized = (status) =>
        typeof status === "string" ? status.toUpperCase() : status;

    const passed = testCases.filter(
        (tc) => normalized(tc.status) === "COMPLETED"
    ).length;

    const failed = testCases.filter((tc) => {
        const st = normalized(tc.status);
        return st === "FAILED" || st === "INITIAL_FAILURE";
    }).length;

    const inProgress = testCases.filter(
        (tc) => normalized(tc.status) === "RUNNING"
    ).length;

    const pending = testCases.filter(
        (tc) => normalized(tc.status) === "PENDING"
    ).length;

    const draft = testCases.filter((tc) => {
        const st = normalized(tc.status);
        return st === "DRAFT" || !st;
    }).length;

    return { passed, failed, inProgress, pending, draft };
};

const getFolderStatusColor = (passed, failed, total) => {
    if (passed === total) return "#F1F8E9";
    if (failed > 0) return "#FEEBEE";
    return "#EEEEEE";
};

const normalizeStatus = (status) =>
    typeof status === "string" ? status.toUpperCase() : status;

const TestsHierarchicalList = ({
    projectId,
    testCases,
    activeItem,
    setActiveItem,
    setTestCasesToggler,
    panelWidth,
}) => {
    const getFolderTestCases = (folder) =>
        (Array.isArray(folder?.testCases) && folder.testCases) ||
        (Array.isArray(folder?.test_cases) && folder.test_cases) ||
        [];

    const [anchorEl, setAnchorEl] = useState(null);
    const { selectedProject } = useProject();
    const [dropdownElInfo, setDropdownElInfo] = useState({
        id: "",
        name: "",
        class: "",
        isBlueprint: false,
    });
    const [createVersionOpen, setCreateVersionOpen] = useState(false);
    const [editVersionOpen, setEditVersionOpen] = useState(false);
    const [deleteVersionOpen, setDeleteVersionOpen] = useState(false);
    const [createCycleOpen, setCreateCycleOpen] = useState(false);
    const [editCycleOpen, setEditCycleOpen] = useState(false);
    const [deleteCycleOpen, setDeleteCycleOpen] = useState(false);
    const [createFolderOpen, setCreateFolderOpen] = useState(false);
    const [editFolderOpen, setEditFolderOpen] = useState(false);
    const [deleteFolderOpen, setDeleteFolderOpen] = useState(false);
    const [addTestsOpen, setAddTestsOpen] = useState(false);
    const [editTestCaseOpen, setEditTestCaseOpen] = useState(false);
    const [deleteTestCaseOpen, setDeleteTestCaseOpen] = useState(false);
    const [expandedSections, setExpandedSections] = useState(new Set());
    const [running, setRunning] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [discoveringPages, setDiscoveringPages] = useState(new Set());
    const [journeyRegenerateOpen, setJourneyRegenerateOpen] = useState(false);
    const [journeyRegenerateConfig, setJourneyRegenerateConfig] =
        useState(null);
    const [journeyRegenerateLoading, setJourneyRegenerateLoading] =
        useState(false);
    const [journeyRegenerateContext, setJourneyRegenerateContext] = useState({
        requirements: "",
        expectedTestCases: "",
    });
    const [folderRunTasks, setFolderRunTasks] = useState({});
    const getFolderKey = (folder) => {
        if (!folder) return "";
        return folder.cycleId ? `${folder.cycleId}-${folder.id}` : `blueprint-${folder.id}`;
    };
    const [testCaseRunTasks, setTestCaseRunTasks] = useState({});

    const blueprintVersion = testCases.find(
        (v) => v.id === "blueprints" || v.name === "Page Blueprints"
    );
    const journeyBlueprintVersion = testCases.find(
        (v) => v.id === "journey-blueprints" || v.name === "Journey Blueprints"
    );
    const blueprintFolders =
        blueprintVersion?.cycles?.[0]?.folders && Array.isArray(blueprintVersion.cycles[0].folders)
            ? blueprintVersion.cycles[0].folders
            : [];
    const journeyFolders =
        journeyBlueprintVersion?.cycles?.[0]?.folders &&
            Array.isArray(journeyBlueprintVersion.cycles[0].folders)
            ? journeyBlueprintVersion.cycles[0].folders
            : [];
    const combinedFolders = [...blueprintFolders, ...journeyFolders];

    const open = Boolean(anchorEl);

    useEffect(() => {
        const generalFolderIds = [];
        (testCases || []).forEach((version) => {
            (version.cycles || []).forEach((cycle) => {
                if (cycle.type === "general" && Array.isArray(cycle.folders)) {
                    cycle.folders.forEach((folder) => {
                        generalFolderIds.push(folder.id);
                    });
                }
            });
        });
        if (!generalFolderIds.length) return;
    }, [testCases]);

    useEffect(() => {
        if (!projectId) return;
        const storageKey = `testsHierarchyExpanded:${projectId}`;
        const saved = sessionStorage.getItem(storageKey);

        if (!saved) return;
        try {
            const ids = JSON.parse(saved);
            if (Array.isArray(ids)) {
                setExpandedSections(new Set(ids));
            }
        } catch (error) {
            console.warn("Failed to parse expanded sections from sessionStorage", error);
        }
    }, [projectId]);

    useEffect(() => {
        const refreshOnEvent = () => setTestCasesToggler((prev) => !prev);
        socketEventEmitter.on("taskActivated", refreshOnEvent);
        socketEventEmitter.on("taskCompleted", refreshOnEvent);
        socketEventEmitter.on("taskFailed", refreshOnEvent);
        return () => {
            socketEventEmitter.off("taskActivated", refreshOnEvent);
            socketEventEmitter.off("taskCompleted", refreshOnEvent);
            socketEventEmitter.off("taskFailed", refreshOnEvent);
        };
    }, [setTestCasesToggler]);

    // Track test-case discovery (generation) tasks per page,
    // same mechanism as usePageTasksStatus (used by the Pages "Regenerate tests" button).
    useEffect(() => {
        if (!selectedProject?.id) return;

        const fetchTasks = async () => {
            try {
                const res = await TaskService.getAllTasksByProjectId(
                    selectedProject.id
                );
                const next = new Set();
                res.tasks.forEach((task) => {
                    const isInProgressOrPending =
                        task.status === TASK_STATUSES.IN_PROGRESS ||
                        task.status === TASK_STATUSES.PENDING;
                    if (
                        isInProgressOrPending &&
                        task.page_id &&
                        task.task_type === TASK_TYPES.TEST_CASE_DISCOVERY
                    ) {
                        next.add(task.page_id);
                    }
                });
                setDiscoveringPages(next);
            } catch (error) {
                console.error("Error fetching tasks:", error);
            }
        };

        fetchTasks();
    }, [selectedProject?.id]);

    useEffect(() => {
        if (!selectedProject?.id) return;

        const updatePageStatus = (type, pageId, inProgress) => {
            if (
                type === TASK_TYPES.TEST_CASE_DISCOVERY ||
                type === TASK_ALIAS_TYPES.DISCOVER_TEST_CASES
            ) {
                setDiscoveringPages((prev) => {
                    const next = new Set(prev);
                    if (inProgress) {
                        next.add(pageId);
                    } else {
                        next.delete(pageId);
                    }
                    return next;
                });
            }
        };

        const handleNewTask = ({ type, pageId }) => {
            if (!pageId) return;
            updatePageStatus(type, pageId, true);
        };

        const handleTaskDone = ({ type, pageId, taskId }) => {
            if (pageId) {
                updatePageStatus(type, pageId, false);
            }
            if (taskId) {
                setFolderRunTasks((prev) => {
                    let changed = false;
                    const next = { ...prev };
                    for (const key of Object.keys(next)) {
                        if (Array.isArray(next[key]) && next[key].includes(taskId)) {
                            next[key] = next[key].filter(id => id !== taskId);
                            if (next[key].length === 0) {
                                delete next[key];
                            }
                            changed = true;
                        }
                    }
                    return changed ? next : prev;
                });
                setTestCaseRunTasks((prev) => {
                    let changed = false;
                    const next = { ...prev };
                    for (const key of Object.keys(next)) {
                        if (Array.isArray(next[key]) && next[key].includes(taskId)) {
                            next[key] = next[key].filter(id => id !== taskId);
                            if (next[key].length === 0) {
                                delete next[key];
                            }
                            changed = true;
                        }
                    }
                    return changed ? next : prev;
                });
            }
        };

        socketEventEmitter.on("newTaskAdded", handleNewTask);
        socketEventEmitter.on("taskCompleted", handleTaskDone);
        socketEventEmitter.on("taskFailed", handleTaskDone);

        return () => {
            socketEventEmitter.off("newTaskAdded", handleNewTask);
            socketEventEmitter.off("taskCompleted", handleTaskDone);
            socketEventEmitter.off("taskFailed", handleTaskDone);
        };
    }, [selectedProject?.id]);

    const isSectionExpanded = (sectionId) => expandedSections.has(sectionId);

    const toggleFolder = (sectionId) => {
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(sectionId)) {
                next.delete(sectionId);
            } else {
                next.add(sectionId);
            }
            const storageKey = `testsHierarchyExpanded:${projectId}`;
            sessionStorage.setItem(
                storageKey,
                JSON.stringify(Array.from(next))
            );
            return next;
        });
    };

    const collectIdsFromFolders = (folders = []) => {
        const ids = new Set();
        folders.forEach((folder) => {
            getFolderTestCases(folder).forEach((tc) => {
                if (tc?.id) ids.add(tc.id);
            });
        });
        return Array.from(ids);
    };

    const runTests = async (ids = [], options = {}) => {
        if (!ids.length || running) return;
        try {
            setRunning(true);
            await PageService.runTestCases(ids, options);
            setTestCasesToggler((prev) => !prev);
        } catch (error) {
            console.error("Failed to run test cases:", error);
        } finally {
            setRunning(false);
        }
    };

    const runTestsForFolder = async (folder, ids = [], options = {}) => {
        if (!folder || !ids.length) return;
        try {
            const res = await PageService.runTestCases(ids, options);
            const results = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
            const taskIds = results
                .map((item) => item?.taskId)
                .filter(Boolean);
            if (taskIds.length) {
                setFolderRunTasks((prev) => {
                    const next = { ...prev };
                    const key = getFolderKey(folder);
                    const existing = new Set(next[key] || []);
                    taskIds.forEach((tid) => existing.add(tid));
                    next[key] = Array.from(existing);
                    return next;
                });
            }
            if (typeof setTestCasesToggler === "function") {
                setTestCasesToggler((prev) => !prev);
            }
        } catch (error) {
            console.error("Failed to run test cases for folder:", error);
        }
    };

    const stopFolderRuns = async (folder) => {
        if (!folder) return;
        const key = getFolderKey(folder);
        let taskIds = folderRunTasks[key] || [];

        if (!taskIds.length && selectedProject?.id) {
            try {
                const folderTestCaseIds = new Set(
                    getFolderTestCases(folder).map((tc) => String(tc?.id)).filter(Boolean)
                );
                if (!folderTestCaseIds.size) return;
                const res = await TaskService.getAllTasksByProjectId(
                    selectedProject.id
                );
                const tasks = res?.tasks || [];
                taskIds = tasks
                    .filter((task) => {
                        const isInProgressOrPending =
                            task.status === TASK_STATUSES.IN_PROGRESS ||
                            task.status === TASK_STATUSES.PENDING;
                        const isTestCaseTask =
                            task.task_type === "Test Case";
                        const tcId = task?.data?.testCase?.id ?? task?.data?.testCaseId;
                        return (
                            isInProgressOrPending &&
                            isTestCaseTask &&
                            tcId &&
                            folderTestCaseIds.has(String(tcId))
                        );
                    })
                    .map((t) => t.id);
            } catch (error) {
                console.error("Error fetching tasks for folder stop:", error);
                return;
            }
        }

        if (!taskIds.length) {
            setFolderRunTasks((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            if (typeof setTestCasesToggler === "function") {
                setTestCasesToggler((prev) => !prev);
            }
            return;
        }

        try {
            await Promise.all(
                taskIds.map((taskId) =>
                    TaskService.stopTaskById(taskId).catch(() => null)
                )
            );
            if (typeof setTestCasesToggler === "function") {
                setTestCasesToggler((prev) => !prev);
            }
        } catch (error) {
            console.error("Failed to stop folder test runs:", error);
        } finally {
            setFolderRunTasks((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    };

    const stopDiscoveryForPage = async (pageId) => {
        if (!pageId || !selectedProject?.id) return;
        try {
            const res = await TaskService.getAllTasksByProjectId(
                selectedProject.id
            );
            const tasks = res?.tasks || [];
            const taskIds = tasks
                .filter((task) => {
                    const isInProgressOrPending =
                        task.status === TASK_STATUSES.IN_PROGRESS ||
                        task.status === TASK_STATUSES.PENDING;
                    const isDiscoveryTask =
                        task.task_type === TASK_TYPES.TEST_CASE_DISCOVERY;
                    return (
                        isInProgressOrPending &&
                        isDiscoveryTask &&
                        String(task.page_id) === String(pageId)
                    );
                })
                .map((task) => task.id);

            if (!taskIds.length) return;

            await Promise.all(
                taskIds.map((taskId) =>
                    TaskService.stopTaskById(taskId).catch(() => null)
                )
            );

            setDiscoveringPages((prev) => {
                const next = new Set(prev);
                next.delete(pageId);
                return next;
            });
            if (typeof setTestCasesToggler === "function") {
                setTestCasesToggler((prev) => !prev);
            }
        } catch (error) {
            console.error("Failed to stop folder regeneration tasks:", error);
        }
    };

    const handleRunVersion = async (versionItem) => {
        if (!versionItem) return;
        // General versions (blueprints/journey blueprints)
        if (versionItem.type === "general") {
            const ids = collectIdsFromFolders(
                (versionItem.cycles || []).flatMap((c) => c.folders || [])
            );
            await runTests(ids);
            return;
        }

        // Custom versions: run per cycle with proper context
        for (const cycle of versionItem.cycles || []) {
            const ids = collectIdsFromFolders(cycle.folders || []);
            await runTests(ids, {
                originType: "cycle",
                testCycleId: cycle.id,
                projectVersionId: versionItem.id,
            });
        }
    };

    const handleRunCycle = async (cycle) => {
        if (!cycle) return;
        const ids = collectIdsFromFolders(cycle.folders || []);
        const options =
            cycle.type === "custom"
                ? {
                    originType: "cycle",
                    testCycleId: cycle.id,
                    projectVersionId: cycle.versionId,
                }
                : {};
        await runTests(ids, options);
    };

    const handleRunFolder = async (folder) => {
        if (!folder) return;
        const ids = collectIdsFromFolders([folder]);
        const options = folder.cycleId
            ? {
                originType: "cycle",
                testCycleId: folder.cycleId,
                projectVersionId: folder.versionId,
            }
            : {};
        await runTestsForFolder(folder, ids, options);
    };

    const handleMenuOpen = (event, elInfo) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setDropdownElInfo(elInfo);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleItemClick = (id, type, extra = {}) => {
        setActiveItem({ id, type, ...extra });
    };

    const handleCreateVersionModalOpen = () => {
        setCreateVersionOpen(true);
    };

    const handleEditVersionModalOpen = () => {
        setEditVersionOpen(true);
        handleMenuClose();
    };

    const handleDeleteVersionModalOpen = () => {
        setDeleteVersionOpen(true);
        handleMenuClose();
    };

    const handleCreateCycleModalOpen = (isCommon = false) => {
        if (isCommon) {
            setDropdownElInfo({
                id: "",
                name: "",
                class: "",
                isBlueprint: false,
            });
        } else {
            handleMenuClose();
        }
        setCreateCycleOpen(true);
    };

    const handleEditCycleModalOpen = () => {
        setEditCycleOpen(true);
        handleMenuClose();
    };

    const handleDeleteCycleModalOpen = () => {
        setDeleteCycleOpen(true);
        handleMenuClose();
    };

    const handleCreateFolderModalOpen = () => {
        setCreateFolderOpen(true);
        handleMenuClose();
    };

    const handleEditFolderModalOpen = () => {
        setEditFolderOpen(true);
        handleMenuClose();
    };

    const handleDeleteFolderModalOpen = () => {
        setDeleteFolderOpen(true);
        handleMenuClose();
    };

    const handleAddTestsModalOpen = () => {
        setAddTestsOpen(true);
        handleMenuClose();
    };

    const handleEditTestCaseModalOpen = () => {
        setEditTestCaseOpen(true);
        handleMenuClose();
    };

    const handleDeleteTestCaseModalOpen = () => {
        setDeleteTestCaseOpen(true);
        handleMenuClose();
    };

    const loadJourneyContext = async (journeyId) => {
        if (!journeyId) return;
        try {
            const journey = await JourneyService.getJourneyById(journeyId);
            setJourneyRegenerateContext({
                requirements: journey?.user_requirements || "",
                expectedTestCases: journey?.user_expected_test_cases || "",
            });
        } catch (error) {
            console.error("Failed to load journey context:", error);
            setJourneyRegenerateContext({
                requirements: "",
                expectedTestCases: "",
            });
        }
    };

    const handleRegenerateTests = async () => {
        if (
            regenerating ||
            journeyRegenerateLoading ||
            !dropdownElInfo?.id
        ) {
            return;
        }
        const { id, class: infoClass, folderType, journeyId } =
            dropdownElInfo;

        if (infoClass === "folder" && folderType === "journey") {
            handleMenuClose();
            setJourneyRegenerateContext({
                requirements: "",
                expectedTestCases: "",
            });
            setJourneyRegenerateConfig({
                journeyId: id,
                regenerateAll: true,
            });
            setJourneyRegenerateOpen(true);
            await loadJourneyContext(id);
            return;
        }

        if (infoClass === "testCase" && journeyId) {
            handleMenuClose();
            setJourneyRegenerateContext({
                requirements: "",
                expectedTestCases: "",
            });
            setJourneyRegenerateConfig({
                journeyId,
                idsToRegenerate: [id],
                regenerateAll: false,
            });
            setJourneyRegenerateOpen(true);
            await loadJourneyContext(journeyId);
            return;
        }

        try {
            setRegenerating(true);
            handleMenuClose();

            if (infoClass === "folder") {
                if (folderType === "journey") {
                    await JourneyTestCaseService.regenerateTestCases({
                        journeyId: id,
                        regenerateAll: true,
                    });
                } else {
                    await PageService.discoverTestCasesByPageId(id);
                }
            } else if (infoClass === "testCase") {
                await PageService.regenerateTestCasesById(id);
            }

            setTestCasesToggler((prev) => !prev);
        } catch (error) {
            console.error("Failed to regenerate tests:", error);
        } finally {
            setRegenerating(false);
        }
    };

    const closeJourneyRegenerateModal = () => {
        setJourneyRegenerateOpen(false);
        setJourneyRegenerateConfig(null);
    };

    const handleJourneyRegenerate = async (contextData = null) => {
        if (!journeyRegenerateConfig?.journeyId) return;
        try {
            setJourneyRegenerateLoading(true);
            await JourneyTestCaseService.regenerateTestCases({
                journeyId: journeyRegenerateConfig.journeyId,
                idsToRegenerate: journeyRegenerateConfig.idsToRegenerate,
                regenerateAll: journeyRegenerateConfig.regenerateAll,
                contextData,
            });
            setTestCasesToggler((prev) => !prev);
        } catch (error) {
            console.error("Failed to regenerate journey tests:", error);
        } finally {
            setJourneyRegenerateLoading(false);
        }
    };

    const renderTestCase = (tc, cycleId = null, folderContext = null) => {
        const normalizedCycleId =
            tc.cycleId || tc.testCycleId || tc.test_cycle_id || cycleId;
        const parentItem =
            folderContext && Object.keys(folderContext).length
                ? { ...folderContext }
                : tc.pageId
                    ? {
                        type: "folder",
                        id: tc.pageId,
                        pageId: tc.pageId,
                        cycleId: normalizedCycleId,
                        versionId: tc.versionId,
                    }
                    : null;
        const publicId = tc.publicId || tc.public_id || null;
        const displayName = truncate(tc.name, 40);
        const label = publicId ? `${publicId} — ${displayName}` : displayName;
        const journeyId = tc.journeyId || tc.journey_id || null;
        const folderType = journeyId ? "journey" : "page";

        const rawTcStatus = normalizeStatus(tc.status) || "DRAFT";
        // Always reflect real status on each row. Do not map PENDING→RUNNING here:
        // when the user stops the only RUNNING test, remaining PENDING must stay
        // pending (orange), not flip to fake "in progress" (blue).
        const tcStatus = rawTcStatus;
        const canStopSingleTestCase =
            rawTcStatus === "RUNNING" || rawTcStatus === "PENDING";
        const tcStatusColor =
            tcStatus === 'COMPLETED'
                ? '#8BC34A'
                : tcStatus === 'FAILED' || tcStatus === 'INITIAL_FAILURE'
                    ? '#F44336'
                    : tcStatus === 'RUNNING'
                        ? '#03A9F4'
                        : tcStatus === 'PENDING'
                            ? '#F0AD4E'
                            : '#9E9E9E';

        return (
            <TestsHierarchicalListRow
                key={`folder-tc-${tc.id}`}
                level={11.2}
                onClick={() =>
                    handleItemClick(tc.id, "testCase", {
                        cycleId: normalizedCycleId,
                        cycleItemId: tc.cycleItemId,
                        pageId: tc.pageId,
                        versionId: tc.versionId,
                        parentItem,
                    })
                }
                isActiveFolder={activeItem?.id === tc.id}
            >
                <Box className="testCasesHierarchicalListFolderTitle">
                    <ListAltOutlinedIcon className="testCasesHierarchicalListFolderIcon" />
                    <Box className="testCasesHierarchicalListFolderTitleContent">
                        <Box className="labelContainer">
                            <Tooltip
                                title={publicId ? `${publicId} — ${tc.name}` : tc.name}
                                PopperProps={{
                                    sx: {
                                        "& .MuiTooltip-tooltip": {
                                            backgroundColor: "primary.main",
                                        },
                                    },
                                }}
                            >
                                <Typography>{label}</Typography>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
                <Box className="testCasesHierarchicalListFolderActions">
                    <Box
                        className="testCaseStatusDot"
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: tcStatusColor,
                            flexShrink: 0,
                        }}
                    />
                    <IconButton
                        className="folderActionContainer"
                        onClick={async (e) => {
                            e.stopPropagation();
                            const key = String(tc.id);

                            if (canStopSingleTestCase) {
                                // Stop queued or active tasks for this test case.
                                // Prefer locally stored taskIds, otherwise fall back
                                // to querying project tasks (similar to /tasks page).
                                let taskIds = testCaseRunTasks[key] || [];

                                if (
                                    (!Array.isArray(taskIds) ||
                                        taskIds.length === 0) &&
                                    selectedProject?.id
                                ) {
                                    try {
                                        const res =
                                            await TaskService.getAllTasksByProjectId(
                                                selectedProject.id
                                            );
                                        const tasks = res?.tasks || [];
                                        taskIds = tasks
                                            .filter((task) => {
                                                const isInProgressOrPending =
                                                    task.status ===
                                                    TASK_STATUSES.IN_PROGRESS ||
                                                    task.status ===
                                                    TASK_STATUSES.PENDING;
                                                const isTestCaseTask =
                                                    task.task_type ===
                                                    "Test Case";
                                                const testCaseIdFromTask =
                                                    task?.data?.testCase?.id ??
                                                    task?.data?.testCaseId;
                                                return (
                                                    isInProgressOrPending &&
                                                    isTestCaseTask &&
                                                    testCaseIdFromTask &&
                                                    String(
                                                        testCaseIdFromTask
                                                    ) === String(tc.id)
                                                );
                                            })
                                            .map((task) => task.id);
                                    } catch (error) {
                                        console.error(
                                            "Error fetching tasks for stop:",
                                            error
                                        );
                                    }
                                }

                                if (!taskIds || !taskIds.length) return;
                                try {
                                    await Promise.all(
                                        taskIds.map((taskId) =>
                                            TaskService.stopTaskById(taskId).catch(
                                                () => null
                                            )
                                        )
                                    );
                                } catch (error) {
                                    console.error(
                                        "Error stopping test case run tasks:",
                                        error
                                    );
                                } finally {
                                    setTestCaseRunTasks((prev) => {
                                        const next = { ...prev };
                                        delete next[key];
                                        return next;
                                    });
                                    if (typeof setTestCasesToggler === "function") {
                                        setTestCasesToggler((prev) => !prev);
                                    }
                                }
                                return;
                            }

                            try {
                                const options = cycleId
                                    ? {
                                        originType: "cycle",
                                        testCycleId: cycleId,
                                        projectVersionId: tc.versionId,
                                    }
                                    : {};
                                const res = await PageService.runTestCases(
                                    [tc.id],
                                    options
                                );
                                const results = Array.isArray(res?.data)
                                    ? res.data
                                    : Array.isArray(res)
                                        ? res
                                        : [];
                                const taskIds = results
                                    .map((item) => item?.taskId)
                                    .filter(Boolean);
                                if (taskIds.length) {
                                    setTestCaseRunTasks((prev) => ({
                                        ...prev,
                                        [key]: taskIds,
                                    }));
                                    if (typeof setTestCasesToggler === "function") {
                                        setTestCasesToggler((prev) => !prev);
                                    }
                                }
                            } catch (error) {
                                console.error(
                                    "Error executing test case run:",
                                    error
                                );
                            }
                        }}
                    >
                        <Tooltip
                            title={
                                canStopSingleTestCase ? "Stop" : "Re-Run"
                            }
                            PopperProps={{
                                sx: {
                                    "& .MuiTooltip-tooltip": {
                                        backgroundColor: "primary.main",
                                    },
                                },
                            }}
                        >
                            <Box>
                                {canStopSingleTestCase ? (
                                    <StopCircleOutlinedIcon color="primary" />
                                ) : (
                                    <RerunIcon color="#2D4356" />
                                )}
                            </Box>
                        </Tooltip>
                    </IconButton>
                    <IconButton
                        className="folderActionContainer"
                        onClick={(e) =>
                            handleMenuOpen(e, {
                                id: tc.id,
                                name: tc.name,
                                class: "testCase",
                                isBlueprint: !tc.cycleId && !cycleId,
                                cycleItemId: tc.cycleItemId,
                                cycleId: tc.cycleId || cycleId,
                                versionId: tc.versionId,
                                journeyId,
                                folderType,
                                testCases:
                                    folderContext?.testCases,
                            })
                        }
                    >
                        <MoreVertIcon color="primary" />
                    </IconButton>
                </Box>
            </TestsHierarchicalListRow>
        );
    };

    return (
        <Box
            className="testCasesHierarchicalListContainer"
            sx={{ width: panelWidth, minWidth: panelWidth }}
        >
            <Box className="testCasesHierarchicalListTitleContainer">
                <Typography className="testCasesHierarchicalListTitleText">
                    Tests
                </Typography>
                <Box className="testCasesHierarchicalListTitleActions">
                    <Button
                        className="testCasesHierarchicalListTitleActionButton"
                        variant="outlined"
                        color="secondary"
                        onClick={handleCreateVersionModalOpen}
                    >
                        <TestListAddVersionIcon />
                        <span className="testCasesHierarchicalListTitleActionButtonText">
                            VERSION
                        </span>
                    </Button>
                    <Button
                        className="testCasesHierarchicalListTitleActionButton"
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleCreateCycleModalOpen(true)}
                    >
                        <TestListAddCycleIcon />
                        <span className="testCasesHierarchicalListTitleActionButtonText">
                            CYCLE
                        </span>
                    </Button>
                </Box>
            </Box>
            {testCases.map((versionItem) => {
                const hasContent = versionItem.totalTestCases > 0 ||
                    versionItem.cycles?.some(c => c.type !== "general");
                return (
                    <Accordion
                        key={`version-${versionItem.id}`}
                        className="testCasesHierarchicalList"
                        defaultExpanded
                    >
                        <AccordionSummary
                            className="testCasesHierarchicalListVersionTitle"
                            expandIcon={
                                hasContent ? (
                                    <ExpandMoreIcon color="primary" />
                                ) : null
                            }
                            sx={
                                !hasContent
                                    ? { cursor: "default !important" }
                                    : {
                                        pointerEvents: "none",
                                        "& .MuiAccordionSummary-expandIconWrapper": {
                                            pointerEvents: "auto",
                                            cursor: "pointer",
                                        },
                                        "& .testCasesHierarchicalListFolderTitleActions": {
                                            pointerEvents: "auto",
                                        },
                                    }
                            }
                        >
                            <TestsHierarchicalListRow level={0}>
                                <Box className="testCasesHierarchicalListVersionTitleContent">
                                    {versionItem.type === "general" ? (
                                        <TestListBlueprintsVersionIcon className="testCasesHierarchicalListVersionTitleIcon" />
                                    ) : (
                                        <TestListVersionIcon className="testCasesHierarchicalListVersionTitleIcon" />
                                    )}
                                    <Typography className="testCasesHierarchicalListVersionTitleText">
                                        {versionItem.name}
                                    </Typography>
                                </Box>
                                <Box className="testCasesHierarchicalListFolderTitleActions">
                                    <Box className="folderActionContainer">
                                        <Typography className="itemsTotal">
                                            {versionItem.totalTestCases}
                                        </Typography>
                                    </Box>
                                    <Tooltip
                                        title={
                                            !versionItem.totalTestCases
                                                ? "No test cases to run"
                                                : versionItem.type === "general"
                                                    ? "Run All"
                                                    : "Re-Run All"
                                        }
                                        PopperProps={{
                                            sx: {
                                                "& .MuiTooltip-tooltip": {
                                                    backgroundColor:
                                                        "primary.main",
                                                },
                                            },
                                        }}
                                    >
                                        <span>
                                            <IconButton
                                                className="folderActionContainer"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRunVersion(versionItem);
                                                }}
                                                disabled={running || !versionItem.totalTestCases}
                                            >
                                                {versionItem.type === "general" ? (
                                                    <PlayCircleOutlineOutlinedIcon color="primary" />
                                                ) : (
                                                    <RerunIcon color="#2D4356" />
                                                )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    {versionItem.type !== "general" && (
                                        <IconButton
                                            className="folderActionContainer"
                                            onClick={
                                                (e) =>
                                                    handleMenuOpen(e, {
                                                        id: versionItem.id,
                                                        name: versionItem.name,
                                                        class: "version",
                                                        isBlueprint: false,
                                                    })
                                            }
                                        >
                                            <MoreVertIcon color="primary" />
                                        </IconButton>
                                    )}
                                </Box>
                            </TestsHierarchicalListRow>
                        </AccordionSummary>
                        <AccordionDetails className="testCasesHierarchicalListVersionContent">
                            {versionItem.cycles.map((cycle) =>
                                cycle.type === "general" ? (
                                    cycle.folders.map((folder) => {
                                        const folderTestCases = getFolderTestCases(
                                            folder
                                        );
                                        const {
                                            passed,
                                            failed,
                                            inProgress,
                                            pending,
                                            draft,
                                        } = getStatusCounts(folderTestCases);
                                        const total =
                                            passed +
                                            failed +
                                            inProgress +
                                            pending +
                                            draft;
                                        const safeTotal = total || 1;
                                        const isGenerating =
                                            discoveringPages.has(folder.id);
                                        const passedPercent =
                                            isGenerating
                                                ? 0
                                                : (passed / safeTotal) * 100;
                                        const failedPercent =
                                            isGenerating
                                                ? 0
                                                : (failed / safeTotal) * 100;
                                        // Use real counts only. Do not fold pending into
                                        // in-progress when nothing is RUNNING — after stopping
                                        // the active test, remaining PENDING must stay orange on
                                        // the folder bar (matches per-test-case dots).
                                        const inProgressPercent =
                                            isGenerating
                                                ? 100
                                                : (inProgress / safeTotal) * 100;
                                        const pendingPercent =
                                            isGenerating
                                                ? 0
                                                : (pending / safeTotal) * 100;
                                        const draftPercent =
                                            isGenerating
                                                ? 0
                                                : (draft / safeTotal) * 100;
                                        return (
                                            <Box
                                                key={`folder-${folder.id}`}
                                                sx={{ width: "100%" }}
                                            >
                                                <TestsHierarchicalListRow
                                                    level={5}
                                                    onClick={() => {
                                                        handleItemClick(
                                                            folder.id,
                                                            "folder",
                                                            {
                                                                pageId: folder.id,
                                                                versionId:
                                                                    versionItem.id,
                                                                folderType:
                                                                    folder.folderType,
                                                                isBlueprint: true,
                                                                testCases:
                                                                    folderTestCases,
                                                            }
                                                        );
                                                    }}
                                                    isActiveFolder={
                                                        activeItem?.id === folder.id
                                                    }
                                                >
                                                    <Box className="testCasesHierarchicalListFolderTitle">
                                                        <ExpandMoreIcon
                                                            className="testCasesHierarchicalListFolderIcon"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleFolder(folder.id);
                                                            }}
                                                            sx={{
                                                                cursor: "pointer",
                                                                transform:
                                                                    isSectionExpanded(
                                                                        folder.id
                                                                    )
                                                                        ? "rotate(180deg)"
                                                                        : "rotate(0deg)",
                                                                transition:
                                                                    "transform 0.2s ease",
                                                            }}
                                                        />
                                                        <FolderOutlinedIcon className="testCasesHierarchicalListFolderIcon" />
                                                        <Box className="testCasesHierarchicalListFolderTitleContent">
                                                            <Box className="labelContainer">
                                                                <Typography>
                                                                    {folder.name}
                                                                </Typography>
                                                                <Typography
                                                                    className="labelTotal"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            getFolderStatusColor(
                                                                                passed,
                                                                                failed,
                                                                                total
                                                                            ),
                                                                    }}
                                                                >
                                                                    {total}
                                                                </Typography>
                                                            </Box>
                                                            <Box className="labelProgress">
                                                                <Box
                                                                    sx={{
                                                                        width: `${passedPercent}%`,
                                                                        backgroundColor:
                                                                            "#8BC34A",
                                                                    }}
                                                                />
                                                                <Box
                                                                    sx={{
                                                                        width: `${failedPercent}%`,
                                                                        backgroundColor:
                                                                            "#F44336",
                                                                    }}
                                                                />
                                                                <Box
                                                                    sx={{
                                                                        width: `${inProgressPercent}%`,
                                                                        backgroundColor:
                                                                            "#03A9F4",
                                                                    }}
                                                                />
                                                                <Box
                                                                    sx={{
                                                                        width: `${pendingPercent}%`,
                                                                        backgroundColor:
                                                                            "#F0AD4E",
                                                                    }}
                                                                />
                                                                <Box
                                                                    sx={{
                                                                        width: `${draftPercent}%`,
                                                                        backgroundColor:
                                                                            "#9E9E9E",
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                    <Box className="testCasesHierarchicalListFolderActions">
                                                        <IconButton
                                                            className="folderActionContainer"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const key = getFolderKey(folder);
                                                                const hasLocalTasks =
                                                                    Array.isArray(folderRunTasks[key]) &&
                                                                    folderRunTasks[key].length > 0;
                                                                const hasRunningOrPending =
                                                                    inProgress > 0 || pending > 0;
                                                                const isGenerating =
                                                                    discoveringPages.has(folder.id);
                                                                const showStop =
                                                                    hasLocalTasks ||
                                                                    hasRunningOrPending ||
                                                                    isGenerating;
                                                                if (showStop) {
                                                                    if (isGenerating) {
                                                                        stopDiscoveryForPage(
                                                                            folder.id
                                                                        );
                                                                    } else {
                                                                        stopFolderRuns(folder);
                                                                    }
                                                                } else {
                                                                    handleRunFolder(folder);
                                                                }
                                                            }}
                                                            disabled={running}
                                                        >
                                                            <Tooltip
                                                                title={
                                                                    inProgress > 0 || pending > 0 ||
                                                                        discoveringPages.has(folder.id) ||
                                                                        (Array.isArray(
                                                                            folderRunTasks[getFolderKey(folder)]
                                                                        ) &&
                                                                            folderRunTasks[getFolderKey(folder)].length > 0)
                                                                        ? "Stop"
                                                                        : "Run All"
                                                                }
                                                                PopperProps={{
                                                                    sx: {
                                                                        "& .MuiTooltip-tooltip": {
                                                                            backgroundColor:
                                                                                "primary.main",
                                                                        },
                                                                    },
                                                                }}
                                                            >
                                                                <Box>
                                                                    {inProgress > 0 || pending > 0 ||
                                                                        discoveringPages.has(folder.id) ||
                                                                        (Array.isArray(
                                                                            folderRunTasks[getFolderKey(folder)]
                                                                        ) &&
                                                                            folderRunTasks[getFolderKey(folder)]
                                                                                .length > 0) ? (
                                                                        <StopCircleOutlinedIcon color="error" />
                                                                    ) : (
                                                                        <PlayCircleOutlineOutlinedIcon color="primary" />
                                                                    )}
                                                                </Box>
                                                            </Tooltip>
                                                        </IconButton>
                                                        <IconButton
                                                            className="folderActionContainer"
                                                            onClick={(e) =>
                                                                handleMenuOpen(e, {
                                                                    id: folder.id,
                                                                    name: folder.name,
                                                                    class: "folder",
                                                                    isBlueprint: true,
                                                                    folderType:
                                                                        folder.folderType,
                                                                    testCases:
                                                                        folderTestCases,
                                                                })
                                                            }
                                                        >
                                                            <MoreVertIcon color="primary" />
                                                        </IconButton>
                                                    </Box>
                                                </TestsHierarchicalListRow>
                                                {isSectionExpanded(folder.id) &&
                                                    folderTestCases.map((tc) =>
                                                        renderTestCase(tc, null, {
                                                            type: "folder",
                                                            id: folder.id,
                                                            pageId: folder.id,
                                                            cycleId: null,
                                                            versionId:
                                                                versionItem.id,
                                                            isBlueprint: true,
                                                            folderType:
                                                                folder.folderType,
                                                            testCases:
                                                                folderTestCases,
                                                        })
                                                    )}
                                            </Box>
                                        );
                                    })
                                ) : (
                                    <Accordion
                                        key={`cycle-${cycle.id}`}
                                        className="testCasesHierarchicalCycleList"
                                        expanded={
                                            cycle.totalTestCases
                                                ? isSectionExpanded(`cycle-${cycle.id}`)
                                                : true
                                        }
                                        onChange={() => {
                                            if (!cycle.totalTestCases) return;
                                            toggleFolder(`cycle-${cycle.id}`);
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={
                                                cycle.totalTestCases ? (
                                                    <ExpandMoreIcon color="primary" />
                                                ) : null
                                            }
                                            className="testCasesHierarchicalCycleListTitle"
                                            sx={
                                                !cycle.totalTestCases
                                                    ? { cursor: "default !important" }
                                                    : {
                                                        pointerEvents: "none",
                                                        "& .MuiAccordionSummary-expandIconWrapper": {
                                                            pointerEvents: "auto",
                                                            cursor: "pointer",
                                                        },
                                                        "& .testCasesHierarchicalListCycleActions": {
                                                            pointerEvents: "auto",
                                                        },
                                                    }
                                            }
                                        >
                                            <TestsHierarchicalListRow
                                                key={`cycle-${cycle.id}`}
                                                level={8}
                                            >
                                                <Box className="testCasesHierarchicalListCycleTitle">
                                                    <TestListCycleIcon className="testCasesHierarchicalListCycleIcon" />
                                                    <Typography>
                                                        {cycle.name}
                                                    </Typography>
                                                </Box>
                                                <Box className="testCasesHierarchicalListCycleActions">
                                                    <Box className="cycleActionContainer">
                                                        <Typography className="itemsTotal">
                                                            {cycle.totalTestCases}
                                                        </Typography>
                                                    </Box>
                                                    <Tooltip
                                                        title={
                                                            !cycle.totalTestCases
                                                                ? "No test cases to run"
                                                                : "Re-Run All"
                                                        }
                                                        PopperProps={{
                                                            sx: {
                                                                "& .MuiTooltip-tooltip":
                                                                {
                                                                    backgroundColor:
                                                                        "primary.main",
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <span>
                                                            <IconButton
                                                                className="cycleActionContainer"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRunCycle(cycle);
                                                                }}
                                                                disabled={running || !cycle.totalTestCases}
                                                            >
                                                                <RerunIcon color="#2D4356" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <IconButton
                                                        className="cycleActionContainer"
                                                        onClick={(e) =>
                                                            handleMenuOpen(e, {
                                                                id: cycle.id,
                                                                name: cycle.name,
                                                                class: "cycle",
                                                                isBlueprint: false,
                                                                cycleId: cycle.id,
                                                                versionId:
                                                                    cycle.versionId,
                                                            })
                                                        }
                                                    >
                                                        <MoreVertIcon color="primary" />
                                                    </IconButton>
                                                </Box>
                                            </TestsHierarchicalListRow>
                                        </AccordionSummary>
                                        {cycle.totalTestCases > 0 && (
                                            <AccordionDetails className="testCasesHierarchicalCycleListContent">
                                                {cycle.folders.map((folder) => {
                                                    const folderTestCases = getFolderTestCases(
                                                        folder
                                                    );
                                                    const {
                                                        passed,
                                                        failed,
                                                        inProgress,
                                                        pending,
                                                        draft,
                                                    } = getStatusCounts(folderTestCases);
                                                    const total =
                                                        passed +
                                                        failed +
                                                        inProgress +
                                                        pending +
                                                        draft;
                                                    const safeTotal = total || 1;
                                                    const passedPercent =
                                                        (passed / safeTotal) * 100;
                                                    const failedPercent =
                                                        (failed / safeTotal) * 100;
                                                    const inProgressPercent =
                                                        (inProgress / safeTotal) * 100;
                                                    const pendingPercent =
                                                        (pending / safeTotal) * 100;
                                                    const draftPercent =
                                                        (draft / safeTotal) * 100;

                                                    return folder.isRoot ? (
                                                        folderTestCases.map((tc) =>
                                                            renderTestCase(
                                                                tc,
                                                                cycle.id,
                                                                {
                                                                    type: "folder",
                                                                    id: folder.id,
                                                                    pageId: folder.id,
                                                                    cycleId: cycle.id,
                                                                    versionId:
                                                                        cycle.versionId,
                                                                    folderType:
                                                                        folder.folderType,
                                                                    testCases:
                                                                        folderTestCases,
                                                                }
                                                            )
                                                        )
                                                    ) : (
                                                        <Box
                                                            key={`folder-${folder.id}`}
                                                            sx={{ width: "100%" }}
                                                        >
                                                            <TestsHierarchicalListRow
                                                                level={9}
                                                                onClick={() => {
                                                                    handleItemClick(
                                                                        folder.id,
                                                                        "folder",
                                                                        {
                                                                            cycleId:
                                                                                cycle.id,
                                                                            pageId:
                                                                                folder.id,
                                                                            versionId:
                                                                                cycle.versionId,
                                                                            folderType:
                                                                                folder.folderType,
                                                                            testCases:
                                                                                folderTestCases,
                                                                        }
                                                                    );
                                                                }}
                                                                isActiveFolder={
                                                                    activeItem?.id ===
                                                                    folder.id
                                                                }
                                                            >
                                                                <Box className="testCasesHierarchicalListFolderTitle">
                                                                    <ExpandMoreIcon
                                                                        className="testCasesHierarchicalListFolderIcon"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleFolder(`cycle-${cycle.id}-${folder.id}`);
                                                                        }}
                                                                        sx={{
                                                                            cursor: "pointer",
                                                                            transform:
                                                                                expandedSections.has(
                                                                                    `cycle-${cycle.id}-${folder.id}`
                                                                                )
                                                                                    ? "rotate(180deg)"
                                                                                    : "rotate(0deg)",
                                                                            transition:
                                                                                "transform 0.2s ease",
                                                                        }}
                                                                    />
                                                                    <FolderOutlinedIcon className="testCasesHierarchicalListFolderIcon" />
                                                                    <Box className="testCasesHierarchicalListFolderTitleContent">
                                                                        <Box className="labelContainer">
                                                                            <Typography>
                                                                                {
                                                                                    folder.name
                                                                                }
                                                                            </Typography>
                                                                            <Typography
                                                                                className="labelTotal"
                                                                                sx={{
                                                                                    backgroundColor:
                                                                                        getFolderStatusColor(
                                                                                            passed,
                                                                                            failed,
                                                                                            total
                                                                                        ),
                                                                                }}
                                                                            >
                                                                                {total}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box className="labelProgress">
                                                                            <Box
                                                                                sx={{
                                                                                    width: `${passedPercent}%`,
                                                                                    backgroundColor:
                                                                                        "#8BC34A",
                                                                                }}
                                                                            />
                                                                            <Box
                                                                                sx={{
                                                                                    width: `${failedPercent}%`,
                                                                                    backgroundColor:
                                                                                        "#F44336",
                                                                                }}
                                                                            />
                                                                            <Box
                                                                                sx={{
                                                                                    width: `${inProgressPercent}%`,
                                                                                    backgroundColor:
                                                                                        "#03A9F4",
                                                                                }}
                                                                            />
                                                                            <Box
                                                                                sx={{
                                                                                    width: `${pendingPercent}%`,
                                                                                    backgroundColor:
                                                                                        "#F0AD4E",
                                                                                }}
                                                                            />
                                                                            <Box
                                                                                sx={{
                                                                                    width: `${draftPercent}%`,
                                                                                    backgroundColor:
                                                                                        "#9E9E9E",
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                                <Box className="testCasesHierarchicalListFolderActions">
                                                                    <IconButton
                                                                        className="folderActionContainer"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const key = getFolderKey(folder);
                                                                            const hasLocalTasks =
                                                                                Array.isArray(folderRunTasks[key]) &&
                                                                                folderRunTasks[key].length > 0;
                                                                            const hasRunningOrPending =
                                                                                inProgress > 0 || pending > 0;
                                                                            const showStop = hasLocalTasks || hasRunningOrPending;
                                                                            if (showStop) {
                                                                                stopFolderRuns(folder);
                                                                            } else {
                                                                                handleRunFolder(folder);
                                                                            }
                                                                        }}
                                                                        disabled={running}
                                                                    >
                                                                        <Tooltip
                                                                            title={
                                                                                inProgress > 0 || pending > 0 ||
                                                                                    (Array.isArray(
                                                                                        folderRunTasks[getFolderKey(folder)]
                                                                                    ) &&
                                                                                        folderRunTasks[getFolderKey(folder)].length > 0)
                                                                                    ? "Stop"
                                                                                    : "Re-Run All"
                                                                            }
                                                                            PopperProps={{
                                                                                sx: {
                                                                                    "& .MuiTooltip-tooltip":
                                                                                    {
                                                                                        backgroundColor:
                                                                                            "primary.main",
                                                                                    },
                                                                                },
                                                                            }}
                                                                        >
                                                                            <Box>
                                                                                {inProgress > 0 || pending > 0 ||
                                                                                    (Array.isArray(
                                                                                        folderRunTasks[getFolderKey(folder)]
                                                                                    ) &&
                                                                                        folderRunTasks[getFolderKey(folder)]
                                                                                            .length > 0) ? (
                                                                                    <StopCircleOutlinedIcon color="error" />
                                                                                ) : (
                                                                                    <RerunIcon color="#2D4356" />
                                                                                )}
                                                                            </Box>
                                                                        </Tooltip>
                                                                    </IconButton>
                                                                    <IconButton
                                                                        className="folderActionContainer"
                                                                        onClick={(e) =>
                                                                            handleMenuOpen(
                                                                                e,
                                                                                {
                                                                                    id: folder.id,
                                                                                    name: folder.name,
                                                                                    class: "folder",
                                                                                    isBlueprint: false,
                                                                                    folderType:
                                                                                        folder.folderType,
                                                                                    cycleId:
                                                                                        cycle.id,
                                                                                    pageId:
                                                                                        folder.id,
                                                                                    versionId:
                                                                                        cycle.versionId,
                                                                                }
                                                                            )
                                                                        }
                                                                    >
                                                                        <MoreVertIcon color="primary" />
                                                                    </IconButton>
                                                                </Box>
                                                            </TestsHierarchicalListRow>
                                                            {expandedSections.has(`cycle-${cycle.id}-${folder.id}`) &&
                                                                folderTestCases.map((tc) =>
                                                                    renderTestCase(tc, cycle.id, {
                                                                        type: "folder",
                                                                        id: folder.id,
                                                                        pageId: folder.id,
                                                                        cycleId: cycle.id,
                                                                        versionId:
                                                                            cycle.versionId,
                                                                        folderType:
                                                                            folder.folderType,
                                                                        testCases:
                                                                            folderTestCases,
                                                                    })
                                                                )}
                                                        </Box>
                                                    );
                                                })}
                                            </AccordionDetails>
                                        )}
                                    </Accordion>
                                )
                            )}
                        </AccordionDetails>
                    </Accordion >
                );
            })}
            <TestsHierarchicalListDropdown
                anchorEl={anchorEl}
                open={open}
                dropdownElInfo={dropdownElInfo}
                handleMenuClose={handleMenuClose}
                handleRegenerateTests={handleRegenerateTests}
                handleEditVersionModalOpen={handleEditVersionModalOpen}
                handleDeleteVersionModalOpen={handleDeleteVersionModalOpen}
                handleCreateCycleModalOpen={handleCreateCycleModalOpen}
                handleEditCycleModalOpen={handleEditCycleModalOpen}
                handleDeleteCycleModalOpen={handleDeleteCycleModalOpen}
                handleCreateFolderModalOpen={handleCreateFolderModalOpen}
                handleEditFolderModalOpen={handleEditFolderModalOpen}
                handleDeleteFolderModalOpen={handleDeleteFolderModalOpen}
                handleAddTestsModalOpen={handleAddTestsModalOpen}
                handleEditTestCaseModalOpen={handleEditTestCaseModalOpen}
                handleDeleteTestCaseModalOpen={handleDeleteTestCaseModalOpen}
            />
            <CreateVersionModal
                projectId={projectId}
                createVersionOpen={createVersionOpen}
                setCreateVersionOpen={setCreateVersionOpen}
                setTestCasesToggler={setTestCasesToggler}
            />
            <EditVersionModal
                dropdownElInfo={dropdownElInfo}
                editVersionOpen={editVersionOpen}
                setEditVersionOpen={setEditVersionOpen}
                setTestCasesToggler={setTestCasesToggler}
            />
            <DeleteTestCaseVersionConfirmationModal
                dropdownElInfo={dropdownElInfo}
                deleteVersionOpen={deleteVersionOpen}
                setDeleteVersionOpen={setDeleteVersionOpen}
                setTestCasesToggler={setTestCasesToggler}
            />
            <CreateCycleModal
                testCases={testCases}
                dropdownElInfo={dropdownElInfo}
                createCycleOpen={createCycleOpen}
                setCreateCycleOpen={setCreateCycleOpen}
                setTestCasesToggler={setTestCasesToggler}
            />
            <EditCycleModal
                testCases={testCases}
                dropdownElInfo={dropdownElInfo}
                editCycleOpen={editCycleOpen}
                setEditCycleOpen={setEditCycleOpen}
                setTestCasesToggler={setTestCasesToggler}
            />
            <DeleteTestCaseCycleConfirmationModal
                dropdownElInfo={dropdownElInfo}
                deleteCycleOpen={deleteCycleOpen}
                setDeleteCycleOpen={setDeleteCycleOpen}
                setTestCasesToggler={setTestCasesToggler}
            />
            <CreateFolderModal
                blueprintFolders={combinedFolders}
                dropdownElInfo={dropdownElInfo}
                createFolderOpen={createFolderOpen}
                setCreateFolderOpen={setCreateFolderOpen}
                setTestCasesToggler={setTestCasesToggler}
            />
            <EditFolderModal
                dropdownElInfo={dropdownElInfo}
                editFolderOpen={editFolderOpen}
                setEditFolderOpen={setEditFolderOpen}
                setTestCasesToggler={setTestCasesToggler}
            />
            <DeleteTestCaseFolderConfirmationModal
                dropdownElInfo={dropdownElInfo}
                deleteFolderOpen={deleteFolderOpen}
                setDeleteFolderOpen={setDeleteFolderOpen}
                setTestCasesToggler={setTestCasesToggler}
            />
            <AddTestsModal
                elInfo={dropdownElInfo}
                testCases={testCases}
                addTestsOpen={addTestsOpen}
                setAddTestsOpen={setAddTestsOpen}
                setTestCasesToggler={setTestCasesToggler}
            />
            {
                editTestCaseOpen && (
                    <EditTestCaseModal
                        open={editTestCaseOpen}
                        onClose={() => setEditTestCaseOpen(false)}
                        testCase={dropdownElInfo}
                        onSuccess={() => setTestCasesToggler(prev => !prev)}
                    />
                )
            }
            <DeleteTestCaseFromCycleConfirmationModal
                dropdownElInfo={dropdownElInfo}
                deleteTestCaseOpen={deleteTestCaseOpen}
                setDeleteTestCaseOpen={setDeleteTestCaseOpen}
                setTestCasesToggler={setTestCasesToggler}
            />
            <CreateTestCaseModal
                open={journeyRegenerateOpen}
                onClose={closeJourneyRegenerateModal}
                userRequirements={journeyRegenerateContext.requirements}
                userExpectedTestCases={
                    journeyRegenerateContext.expectedTestCases
                }
                mode="regenerate"
                onGenerate={handleJourneyRegenerate}
                isGeneration={journeyRegenerateLoading}
            />
        </Box >
    );
};

export default TestsHierarchicalList;
